import { effect, Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { pianoAnalytics } from "piano-analytics-js";
import { ConfigService } from "./config";
import { UserStore } from "../../features/store/user-store";

/**
 * Service for configuring and sending events to Piano Analytics.
 */
@Injectable({
  providedIn: "root",
})
export class PianoAnalyticsService {
  readonly router = inject(Router);
  readonly config = inject(ConfigService);
  readonly userStore = inject(UserStore);
  private readonly maxPendingEvents = 20;
  private pendingEvents: Array<{ eventName: string; payload: object }> = [];
  private isConfigured = false;

  constructor() {
    effect(() => {
      if (this.userStore.isUserLoggedIn()) {
        this.configPianoAnalytics();
        this.flushPendingEvents();
      }
    });
  }

  /**
   * Configures Piano Analytics
   */
  configPianoAnalytics(): void {
    if (this.isConfigured) {
      return;
    }

    const pianoConfig = this.config.get("pianoAnalyticsConfig");
    pianoAnalytics.setConfigurations({
      site: pianoConfig["siteId"],
      collectDomain: pianoConfig["collectDomain"],
    });
    this.isConfigured = true;

    if (this.userStore.isUserLoggedIn()) {
      this.flushPendingEvents();
    }
  }

  trackPageDisplay(pageName: string, pagePath?: string): void {
    const resolvedPath = pagePath ?? this.router.url;
    this.sendEvent("page.display", {
      page_name: pageName,
      page_path: resolvedPath,
    });
  }

  sendEvent(eventName: string, payload: object): void {
    if (!this.isConfigured) {
      this.configPianoAnalytics();
    }

    if (!this.userStore.isUserLoggedIn() || !this.isConfigured) {
      this.enqueuePendingEvent({ eventName, payload });
      return;
    }

    this.sendEventToPiano(eventName, payload);
  }

  private enqueuePendingEvent(event: { eventName: string; payload: object }): void {
    if (this.pendingEvents.length >= this.maxPendingEvents) {
      this.pendingEvents = [];
    }

    this.pendingEvents.push(event);
  }

  private flushPendingEvents(): void {
    if (this.pendingEvents.length === 0) {
      return;
    }

    const eventsToSend = [...this.pendingEvents];
    this.pendingEvents = [];

    eventsToSend.forEach((event) => {
      this.sendEventToPiano(event.eventName, event.payload);
    });
  }

  private sendEventToPiano(eventName: string, payload: object): void {
    const enrichedPayload = {
      ...payload,
      division_id: this.userStore.getUserDivisionId(),
      delegation_id: this.userStore.getUserDelegationId(),
      user_area_id: this.userStore.getUserAreaId(),
      division_name: this.userStore.getUserDivisionName(),
      delegation_name: this.userStore.getUserDelegationName(),
    };

    pianoAnalytics.sendEvent(eventName, enrichedPayload);
  }
}
